import {
  ASSISTANT_RATING_REASON_LABELS,
  assistantOutcomes,
  type AdminAssistantReview,
  type AdminAssistantReviewRow,
  type AssistantOutcome,
  type AssistantTopic,
} from "@teamna/shared";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  ChipRow,
  DataTable,
  ErrorState,
  PageHeader,
  SegmentedControl,
  Select,
  StatTile,
  Switch,
  ThumbDownIcon,
  ThumbUpIcon,
  Toolbar,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { ccKeys, fetchAssistantReview, useAdminBusinesses, type AssistantReviewFilters } from "../lib/cc-api.ts";
import { CARD_KIND_WORDS, GAP_KIND_WORDS, OUTCOME_LOOK, PLAN_WORDS, ROLE_WORDS, TOPIC_WORDS, formatUsd } from "./assistant-format.ts";

/**
 * AI Assistant review (`/assistant`, decided 2026-10-21: "full chats, audit-logged"). Every question
 * that somebody asked, of every business, newest first, with how the answer ended: outcome, tools,
 * rating, plan and cost. Teamna staff read it to learn what people ask and what does not work. The
 * filters run on the server; the totals count the whole filter, not the page. A row opens the chat,
 * and that read writes an audit row with the staff email.
 */

const SEARCH_DELAY_MS = 300;
const DAY_OPTIONS = ["7", "14", "30", "90"] as const;
type Days = (typeof DAY_OPTIONS)[number];
type RatingFilter = "" | "up" | "down" | "none";

export function RatingView({ rating, reason }: { rating: "up" | "down" | null; reason: AdminAssistantReviewRow["ratingReason"] }) {
  if (rating === null) return <span className="text-ink-3">None</span>;
  const Icon = rating === "up" ? ThumbUpIcon : ThumbDownIcon;
  return (
    <span className="inline-flex items-start gap-1.5">
      <Icon size={16} className={rating === "up" ? "mt-0.5 shrink-0 text-confirmed" : "mt-0.5 shrink-0 text-cancelled"} />
      <span className="text-[13px]">
        <span className="sr-only">{rating === "up" ? "Thumbs up" : "Thumbs down"}</span>
        {reason ? ASSISTANT_RATING_REASON_LABELS[reason] : null}
      </span>
    </span>
  );
}

export function OutcomePill({ outcome }: { outcome: AssistantOutcome | null }) {
  if (outcome === null) return <Badge tone="quiet">No answer</Badge>;
  const look = OUTCOME_LOOK[outcome];
  return <Badge tone={look.tone}>{look.label}</Badge>;
}

const COLUMNS: DataTableColumn<AdminAssistantReviewRow>[] = [
  {
    id: "when",
    header: "When",
    cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
  },
  {
    id: "business",
    header: "Business",
    cell: (row) => (
      <div className="min-w-[140px]">
        <p dir="auto">{row.businessName}</p>
        <p className="text-[12px] text-ink-2">
          {ROLE_WORDS[row.askerRole]}
          {row.cardKind ? ` · ${CARD_KIND_WORDS[row.cardKind]}` : ""}
        </p>
      </div>
    ),
  },
  {
    id: "prompt",
    header: "Question",
    cell: (row) => (
      <p dir="auto" title={row.prompt} className="line-clamp-2 min-w-[220px] max-w-[420px] break-words">
        {row.inputMode === "voice" ? <span className="me-1 text-[12px] font-semibold text-ink-2">Voice</span> : null}
        {row.prompt}
        {row.gap ? <span className="block text-[12px] text-ink-2">No tool for: {row.gap}</span> : null}
      </p>
    ),
  },
  {
    id: "topic",
    header: "Topic",
    hideOnPhone: true,
    cell: (row) => (row.topic ? TOPIC_WORDS[row.topic] : <span className="text-ink-3">None</span>),
  },
  { id: "outcome", header: "Outcome", cell: (row) => <OutcomePill outcome={row.outcome} /> },
  { id: "rating", header: "Rating", cell: (row) => <RatingView rating={row.rating} reason={row.ratingReason} /> },
  {
    id: "tools",
    header: "Tools",
    hideOnPhone: true,
    cell: (row) => (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={row.tools.join(", ") || undefined}>
        <span className="tabular-nums">{row.tools.length}</span>
        {row.toolError ? <Badge tone="cancelled">Tool error</Badge> : null}
      </span>
    ),
  },
  {
    id: "plan",
    header: "Plan",
    hideOnPhone: true,
    cell: (row) => (row.plan ? PLAN_WORDS[row.plan] : <span className="text-ink-3">None</span>),
  },
  {
    id: "cost",
    header: "Cost",
    align: "end",
    hideOnPhone: true,
    cell: (row) => <span className="tabular-nums">{formatUsd(row.costMicros)}</span>,
  },
];

type Gap = NonNullable<AdminAssistantReview["totals"]["gaps"]>[number];

/** The things no tool answers, the most asked first: the next tools to build. */
const GAP_COLUMNS: DataTableColumn<Gap>[] = [
  { id: "need", header: "Missing", cell: (row) => <span dir="auto">{row.need}</span> },
  { id: "kind", header: "Kind", hideOnPhone: true, cell: (row) => GAP_KIND_WORDS[row.kind] },
  { id: "count", header: "Asked", align: "end", cell: (row) => <span className="tabular-nums">{row.count}</span> },
  { id: "businesses", header: "Businesses", align: "end", hideOnPhone: true, cell: (row) => <span className="tabular-nums">{row.businesses}</span> },
  { id: "last", header: "Last asked", hideOnPhone: true, cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.lastAt)}</span> },
];

export function AssistantReviewScreen() {
  const navigate = useNavigate();
  const businesses = useAdminBusinesses();
  const [days, setDays] = useState<Days>("14");
  // `?business=<id>` from the act-as pages picks the business at the start.
  const { business: linked } = useSearch({ strict: false }) as { business?: unknown };
  const [businessId, setBusinessId] = useState(typeof linked === "string" ? linked : "");
  const [outcome, setOutcome] = useState<AssistantOutcome | "">("");
  const [rating, setRating] = useState<RatingFilter>("");
  const [topic, setTopic] = useState<AssistantTopic | "">("");
  const [problems, setProblems] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  // The server search waits until the typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const filters = useMemo<AssistantReviewFilters>(
    () => ({
      days: Number(days),
      ...(businessId ? { businessId } : {}),
      ...(outcome ? { outcome } : {}),
      ...(rating ? { rating } : {}),
      ...(topic ? { topic } : {}),
      ...(problems ? { problems: "true" as const } : {}),
      ...(search ? { q: search } : {}),
    }),
    [days, businessId, outcome, rating, topic, problems, search],
  );

  const review = useInfiniteQuery({
    queryKey: ccKeys.assistantReview(filters),
    queryFn: ({ pageParam }) => fetchAssistantReview({ ...filters, ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    placeholderData: (previous) => previous,
    staleTime: 30 * 1000,
  });
  const rows = useMemo(() => review.data?.pages.flatMap((page) => page.rows) ?? [], [review.data]);
  const totals = review.data?.pages[0]?.totals;
  const names = useMemo(() => [...(businesses.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [businesses.data]);
  const narrowed = Boolean(businessId || outcome || rating || topic || problems || search);

  const topicOptions = useMemo(
    () => [
      { id: "", label: "All topics" },
      ...(totals?.topics ?? []).flatMap((row) => (row.topic ? [{ id: row.topic, label: TOPIC_WORDS[row.topic], count: row.count }] : [])),
    ],
    [totals],
  );

  const clear = () => {
    setBusinessId("");
    setOutcome("");
    setRating("");
    setTopic("");
    setProblems(false);
    setQuery("");
    setSearch("");
  };

  return (
    <>
      <PageHeader
        title="AI Assistant"
        description="Every question that people asked, newest first, and how each answer ended. Opening a chat is recorded with your email."
        actions={
          <SegmentedControl
            label="Days back"
            value={days}
            onChange={setDays}
            size="compact"
            options={DAY_OPTIONS.map((value) => ({ value, label: `${value} days` }))}
          />
        }
      />

      {review.error && !review.data ? (
        <ErrorState error={review.error} onRetry={() => void review.refetch()} />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
            <StatTile label="Questions" value={totals?.questions ?? 0} />
            <StatTile label="Businesses" value={totals?.businesses ?? 0} />
            <StatTile label="Thumbs up" value={totals?.up ?? 0} />
            <StatTile label="Thumbs down" value={totals?.down ?? 0} tone={(totals?.down ?? 0) > 0 ? "bad" : "neutral"} />
            <StatTile label="Errors" value={totals?.errors ?? 0} tone={(totals?.errors ?? 0) > 0 ? "bad" : "neutral"} />
            <StatTile label="Refused" value={totals?.refused ?? 0} />
            <StatTile label="No tool" value={totals?.noTool ?? 0} />
            <StatTile label="Cost" value={formatUsd(totals?.costMicros ?? 0)} />
          </div>

          {totals?.gaps?.length ? (
            <div className="mb-5">
              <DataTable
                columns={GAP_COLUMNS}
                rows={totals.gaps}
                rowKey={(row) => row.need}
                rowLabel={(row) => `Show the questions with no tool for ${row.need}`}
                onRowClick={() => setOutcome("no_tool")}
                caption="Questions with no tool"
                density="compact"
                countLabel={(count) => `${count} missing ${count === 1 ? "tool" : "tools"}`}
                empty={{ title: "Nothing missing", message: "Every question had a tool." }}
              />
            </div>
          ) : null}

          <div className="mb-4">
            <ChipRow options={topicOptions} selectedId={topic} onSelect={(id) => setTopic(id as AssistantTopic | "")} label="Filter by topic" wrap />
          </div>

          <Toolbar
            search={{ value: query, onChange: setQuery, placeholder: "Search the questions" }}
            actions={
              narrowed ? (
                <Button variant="ghost" size="sm" onClick={clear}>
                  Clear the filters
                </Button>
              ) : null
            }
          >
            <Select label="Filter by business" value={businessId} onChange={(event) => setBusinessId(event.target.value)} className="sm:max-w-[260px]">
              <option value="">All businesses</option>
              {names.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name}
                </option>
              ))}
            </Select>
            <Select label="Filter by outcome" value={outcome} onChange={(event) => setOutcome(event.target.value as AssistantOutcome | "")} className="sm:max-w-[180px]">
              <option value="">All outcomes</option>
              {assistantOutcomes.map((value) => (
                <option key={value} value={value}>
                  {OUTCOME_LOOK[value].label}
                </option>
              ))}
            </Select>
            <Select label="Filter by rating" value={rating} onChange={(event) => setRating(event.target.value as RatingFilter)} className="sm:max-w-[180px]">
              <option value="">All ratings</option>
              <option value="up">Thumbs up</option>
              <option value="down">Thumbs down</option>
              <option value="none">Not rated</option>
            </Select>
            <span className="inline-flex items-center gap-2 text-[14px] font-medium">
              <Switch label="Only problems" checked={problems} onChange={setProblems} />
              <span aria-hidden="true">Only problems</span>
            </span>
          </Toolbar>

          <DataTable
            columns={COLUMNS}
            rows={rows}
            rowKey={(row) => row.turnId}
            rowLabel={(row) => `${row.businessName}: ${row.prompt}`}
            onRowClick={(row) => void navigate({ to: "/assistant/$conversationId", params: { conversationId: row.conversationId } })}
            isLoading={review.isLoading}
            caption="Questions"
            density="compact"
            countLabel={(count) => `${count} shown`}
            empty={
              narrowed
                ? { title: "No questions match", message: "Change the search or the filters." }
                : { title: "No questions yet", message: "A row shows here when an owner or an admin asks AI Assistant a question." }
            }
          />
          {review.hasNextPage ? (
            <div className="mt-4">
              <Button variant="secondary" loading={review.isFetchingNextPage} onClick={() => void review.fetchNextPage()}>
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
